/**
 * 项目编辑弹窗
 * Tab 式组织：项目信息、企业设置、投标信息、模型选择、知识库、其他附件
 * 各 Tab 内容已提取为独立子组件
 * 注：论文/申报/方案（非投标）项目不展示「投标信息」Tab
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ProjectInfo } from '../types';
import { projectApi, knowledgeApi, modelConfigApi, getErrorMessage, systemConfigApi, companyApi, API_BASE_URL } from '../services/api';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ExtractedTextEditor from './ExtractedTextEditor';
import ProjectInfoTab from './ProjectInfoTab';
import BidderSettingsTab from './BidderSettingsTab';
import BidInfoTab from './BidInfoTab';
import ModelSelectionTab from './ModelSelectionTab';
import KnowledgeBaseSelectTab from './KnowledgeBaseSelectTab';
import ProjectAttachmentsTab from './ProjectAttachmentsTab';

// ---- 类型 ----
import type { KnowledgeBase, ModelConfigItem, CompanyProfile, ProjectAttachment } from '../types';

interface ProjectEditDialogProps {
  project: ProjectInfo;
  isOpen: boolean;
  onClose: () => void;
  onSaved: (project: ProjectInfo) => void;
}

const TABS = [
  { key: 'info', label: '项目信息' },
  { key: 'bid', label: '企业设置' },
  { key: 'basic', label: '投标信息' },
  { key: 'model', label: '模型选择' },
  { key: 'knowledge', label: '知识库' },
  { key: 'attachments', label: '其他附件' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const ProjectEditDialog: React.FC<ProjectEditDialogProps> = ({ project, isOpen, onClose, onSaved }) => {
  // ---- 项目信息 ----
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [descriptiveLevel, setDescriptiveLevel] = useState('detailed');

  // ---- 知识库 ----
  const [knowledgeBaseIds, setKnowledgeBaseIds] = useState<number[]>([]);
  const [ownedKnowledgeBases, setOwnedKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [sharedKnowledgeBases, setSharedKnowledgeBases] = useState<KnowledgeBase[]>([]);

  // ---- 投标信息 ----
  const [ownedCompanies, setOwnedCompanies] = useState<CompanyProfile[]>([]);
  const [sharedCompanies, setSharedCompanies] = useState<CompanyProfile[]>([]);
  const [companyProfileIds, setCompanyProfileIds] = useState<number[]>([]);
  const [bidTotalPrice, setBidTotalPrice] = useState('');
  const [bidDuration, setBidDuration] = useState('');
  const [projectManager, setProjectManager] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [bidAgent, setBidAgent] = useState('');
  const [otherInfo, setOtherInfo] = useState('');

  // ---- 其他附件 ----
  const [projectAttachments, setProjectAttachments] = useState<ProjectAttachment[]>([]);
  const [projectAttLoading, setProjectAttLoading] = useState(false);
  const [uploadingProjectAtt, setUploadingProjectAtt] = useState(false);
  const [projectAttDesc, setProjectAttDesc] = useState('');
  const [projectAttPreviewUrl, setProjectAttPreviewUrl] = useState<string | null>(null);
  const [editingProjectAttDescId, setEditingProjectAttDescId] = useState<number | null>(null);
  const [editingProjectAttDescValue, setEditingProjectAttDescValue] = useState('');
  const [editingProjectAttText, setEditingProjectAttText] = useState<ProjectAttachment | null>(null);
  const [editProjectAttContent, setEditProjectAttContent] = useState('');
  const [editProjectAttLoading, setEditProjectAttLoading] = useState(false);

  // ---- 模型选择 ----
  const [availableModels, setAvailableModels] = useState<ModelConfigItem[]>([]);
  const [userDefaultModelId, setUserDefaultModelId] = useState<number | null>(null);
  const [userDefaultModelName, setUserDefaultModelName] = useState<string>('');
  const [projectModelId, setProjectModelId] = useState<number | null>(null);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [projectAttTextLimit, setProjectAttTextLimit] = useState(2000);

  // ---- 通用 ----
  const [activeTab, setActiveTab] = useState<TabKey>('info');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 初始化
  useEffect(() => {
    if (isOpen && project) {
      setName(project.name);
      setDescription(project.description || '');
      setDescriptiveLevel(project.outline_descriptive_level || 'detailed');
      setKnowledgeBaseIds(project.knowledge_base_ids || []);
      setProjectModelId(project.model_config_id ?? null);
      setCompanyProfileIds(project.company_profile_ids || []);
      setBidTotalPrice(project.bid_total_price || '');
      setBidDuration(project.bid_duration || '');
      setProjectManager(project.project_manager || '');
      setContactPerson(project.contact_person || '');
      setContactPhone(project.contact_phone || '');
      setContactEmail(project.contact_email || '');
      setBidAgent(project.bid_agent || '');
      setOtherInfo(project.other_info || '');
      setActiveTab('info');
      setMessage(null);
      // 同时获取「我的」和「共享的」知识库与企业信息
      knowledgeApi.list().then((res) => setOwnedKnowledgeBases(res.data.knowledge_bases)).catch(() => {});
      knowledgeApi.list('shared').then((res) => setSharedKnowledgeBases(res.data.knowledge_bases)).catch(() => {});
      companyApi.list().then((res) => setOwnedCompanies(res.data.companies)).catch(() => {});
      companyApi.list('shared').then((res) => setSharedCompanies(res.data.companies)).catch(() => {});
      loadModels();
    }
  }, [isOpen, project]);

  const loadModels = useCallback(async () => {
    setModelsLoading(true);
    try {
      const [availRes, currRes, sysCfgRes] = await Promise.all([
        modelConfigApi.getAvailable(), modelConfigApi.getCurrent(), systemConfigApi.getConfig(),
      ]);
      setAvailableModels(availRes.data.configs);
      setUserDefaultModelId(currRes.data.model_config_id ?? null);
      if (currRes.data.config) {
        setUserDefaultModelName(currRes.data.config.model);
      } else       if (sysCfgRes.data.success && sysCfgRes.data.config?.model) {
        setUserDefaultModelName(sysCfgRes.data.config.model);
      }
      // 读取项目附件文本截断限制
      if (sysCfgRes.data.success && sysCfgRes.data.config?.project_att_text_limit) {
        const limit = parseInt(sysCfgRes.data.config.project_att_text_limit, 10);
        if (!isNaN(limit) && limit > 0) setProjectAttTextLimit(limit);
      }
    } catch { /* ignore */ } finally { setModelsLoading(false); }
  }, []);

  // Escape 关闭
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape' && isOpen) onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleSave = async () => {
    if (!name.trim()) return;
    try {
      setSaving(true); setMessage(null);
      const response = await projectApi.update(project.id, {
        name: name.trim(), description: description.trim() || undefined,
        knowledge_base_ids: knowledgeBaseIds, model_config_id: projectModelId ?? 0,
        outline_descriptive_level: descriptiveLevel, company_profile_ids: companyProfileIds,
        bid_total_price: bidTotalPrice || null, bid_duration: bidDuration || null,
        project_manager: projectManager || null, contact_person: contactPerson || null,
        contact_phone: contactPhone || null, contact_email: contactEmail || null,
        bid_agent: bidAgent || null,
        other_info: otherInfo || null,
      });
      onSaved(response.data); onClose();
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '保存失败') });
      setTimeout(() => setMessage(null), 4000);
    } finally { setSaving(false); }
  };

  // 重新加载知识库列表（上传模板后刷新）
  const reloadKnowledgeBases = useCallback(async () => {
    try {
      const [ownedRes, sharedRes] = await Promise.all([
        knowledgeApi.list(),
        knowledgeApi.list('shared'),
      ]);
      setOwnedKnowledgeBases(ownedRes.data.knowledge_bases);
      setSharedKnowledgeBases(sharedRes.data.knowledge_bases);
    } catch { /* ignore */ }
  }, []);

  // ---- 附件操作 ----
  const loadProjectAttachments = useCallback(async (projectId: number) => {
    setProjectAttLoading(true);
    try { setProjectAttachments((await projectApi.listAttachments(projectId)).data.attachments); }
    catch { setProjectAttachments([]); }
    finally { setProjectAttLoading(false); }
  }, []);

  useEffect(() => {
    if (isOpen && project && activeTab === 'attachments') loadProjectAttachments(project.id);
  }, [isOpen, activeTab, project, loadProjectAttachments]);

  const handleProjectAttUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files; if (!files || !files.length || !project) return;
    const fileList = Array.from(files);
    const desc = projectAttDesc.trim() || undefined;
    setUploadingProjectAtt(true);
    try {
      for (let i = 0; i < fileList.length; i++) {
        setMessage({ type: 'success', text: `正在上传 ${i + 1}/${fileList.length}...` });
        await projectApi.uploadAttachment(project.id, fileList[i], desc);
      }
      setProjectAttDesc(''); await loadProjectAttachments(project.id);
      setMessage({ type: 'success', text: `成功上传 ${fileList.length} 个文件` });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: getErrorMessage(err, '上传失败') });
      setTimeout(() => setMessage(null), 4000);
    } finally { setUploadingProjectAtt(false); e.target.value = ''; }
  };

  const handleProjectAttDescSave = async (attId: number) => {
    if (!project) return;
    try {
      await projectApi.updateAttachment(project.id, attId, { description: editingProjectAttDescValue.trim() });
      setEditingProjectAttDescId(null); await loadProjectAttachments(project.id);
    } catch (err) {
      setMessage({ type: 'error', text: getErrorMessage(err, '更新描述失败') });
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const handleProjectAttDelete = async (attId: number, fileName: string) => {
    if (!window.confirm(`确定删除附件「${fileName}」吗？`) || !project) return;
    try { await projectApi.deleteAttachment(project.id, attId); await loadProjectAttachments(project.id); }
    catch (err) { setMessage({ type: 'error', text: getErrorMessage(err, '删除失败') }); setTimeout(() => setMessage(null), 4000); }
  };

  const handleProjectAttEditText = async (att: ProjectAttachment) => {
    if (!project) return;
    setEditProjectAttLoading(true); setEditingProjectAttText(att); setEditProjectAttContent('');
    try { setEditProjectAttContent((await projectApi.getAttachmentDetail(project.id, att.id)).data.extracted_text || ''); }
    catch (err) {
      setMessage({ type: 'error', text: getErrorMessage(err, '获取文件内容失败') });
      setTimeout(() => setMessage(null), 4000); setEditingProjectAttText(null);
    } finally { setEditProjectAttLoading(false); }
  };

  const handleProjectAttSaveText = async () => {
    if (!editingProjectAttText || !project) return;
    try {
      await projectApi.updateAttachment(project.id, editingProjectAttText.id, { extracted_text: editProjectAttContent });
      setMessage({ type: 'success', text: `「${editingProjectAttText.file_name}」内容已保存` });
      setTimeout(() => setMessage(null), 3000);
      setEditingProjectAttText(null); setEditProjectAttContent('');
      await loadProjectAttachments(project.id);
    } catch (err) { setMessage({ type: 'error', text: getErrorMessage(err, '保存失败') }); setTimeout(() => setMessage(null), 4000); }
  };

  const resolveProjectImageUrl = useCallback((url: string) => {
    if (!url || url.startsWith('http')) return url;
    return API_BASE_URL + url;
  }, []);

  // 投标项目展示「投标信息」；论文/申报/方案（非投标）不展示
  const isBid = !project.doc_kind || project.doc_kind === 'bid';
  const visibleTabs = TABS.filter((tab) => isBid || tab.key !== 'basic');

  if (!isOpen) return null;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'info':
        return (
          <ProjectInfoTab name={name} setName={setName} description={description}
            setDescription={setDescription} descriptiveLevel={descriptiveLevel}
            setDescriptiveLevel={setDescriptiveLevel}
            projectId={project.id}
            onSave={handleSave} />
        );
      case 'bid':
        return (
          <BidderSettingsTab
            ownedCompanies={ownedCompanies} sharedCompanies={sharedCompanies}
            companyProfileIds={companyProfileIds}
            setCompanyProfileIds={setCompanyProfileIds} />
        );
      case 'basic':
        return (
          <BidInfoTab bidTotalPrice={bidTotalPrice} setBidTotalPrice={setBidTotalPrice}
            bidDuration={bidDuration} setBidDuration={setBidDuration}
            projectManager={projectManager} setProjectManager={setProjectManager}
            contactPerson={contactPerson} setContactPerson={setContactPerson}
            contactPhone={contactPhone} setContactPhone={setContactPhone}
            contactEmail={contactEmail} setContactEmail={setContactEmail}
            bidAgent={bidAgent} setBidAgent={setBidAgent}
            otherInfo={otherInfo} setOtherInfo={setOtherInfo} />
        );
      case 'model':
        return (
          <ModelSelectionTab modelsLoading={modelsLoading} availableModels={availableModels}
            projectModelId={projectModelId} userDefaultModelId={userDefaultModelId}
            userDefaultModelName={userDefaultModelName} setProjectModelId={setProjectModelId} />
        );
      case 'knowledge':
        return (
          <KnowledgeBaseSelectTab
            ownedKnowledgeBases={ownedKnowledgeBases} sharedKnowledgeBases={sharedKnowledgeBases}
            knowledgeBaseIds={knowledgeBaseIds} setKnowledgeBaseIds={setKnowledgeBaseIds}
            onKbChanged={reloadKnowledgeBases} />
        );
      case 'attachments':
        return (
          <ProjectAttachmentsTab projectAttachments={projectAttachments}
            projectAttLoading={projectAttLoading} uploadingProjectAtt={uploadingProjectAtt}
            projectAttDesc={projectAttDesc} setProjectAttDesc={setProjectAttDesc}
            editingProjectAttDescId={editingProjectAttDescId}
            editingProjectAttDescValue={editingProjectAttDescValue}
            setEditingProjectAttDescValue={setEditingProjectAttDescValue}
            setEditingProjectAttDescId={setEditingProjectAttDescId}
            handleProjectAttUpload={handleProjectAttUpload}
            handleProjectAttDescSave={handleProjectAttDescSave}
            handleProjectAttDelete={handleProjectAttDelete}
            handleProjectAttEditText={handleProjectAttEditText}
            setProjectAttPreviewUrl={setProjectAttPreviewUrl}
            projectAttTextLimit={projectAttTextLimit} />
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-start justify-center pt-[10vh]">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">修改项目</h2>
          <button onClick={onClose} className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Tab 导航 */}
        <div className="flex-shrink-0 px-6 pt-4 border-b border-gray-100">
          <nav className="flex gap-0 -mb-px">
            {visibleTabs.map((tab) => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.key ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab 内容 */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {message && (
            <div className={`mb-4 px-4 py-2.5 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
              {message.text}
            </div>
          )}
          {renderTabContent()}
        </div>

        {/* 底部按钮 */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors">取消</button>
          <button onClick={handleSave} disabled={saving || !name.trim()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>

      {/* 图片预览弹窗 */}
      {projectAttPreviewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 cursor-pointer" onClick={() => setProjectAttPreviewUrl(null)}>
          <button className="absolute top-4 right-4 p-2 rounded-full bg-white/20 text-white hover:bg-white/40 transition-colors"
            onClick={(e) => { e.stopPropagation(); setProjectAttPreviewUrl(null); }}>
            <XMarkIcon className="w-6 h-6" />
          </button>
          <img src={projectAttPreviewUrl} alt="预览" className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* 附件提取文本编辑器 */}
      <ExtractedTextEditor
        open={!!editingProjectAttText && !editProjectAttLoading}
        title={editingProjectAttText?.file_name || ''}
        value={editProjectAttContent}
        onChange={setEditProjectAttContent}
        resolveUrl={resolveProjectImageUrl}
        onSave={handleProjectAttSaveText}
        onClose={() => { setEditingProjectAttText(null); setEditProjectAttContent(''); }}
      />
    </div>
  );
};

export default ProjectEditDialog;
